import { useMutation, useQuery } from "@tanstack/react-query";
import * as React from "react";

import { apiDownload, apiFetch } from "@/api/client";

// Tenant-scoped catalog feed. Each row is one typed-object instance
// the catalog stores per anchor §5.2 (keyed by (tenant_id, content_hash)).

export interface Artifact {
  artifact_id: string;
  content_hash: string;
  // Typed-object type — e.g. "ForceFieldBundle", "Molecule",
  // "StructureState", "FragmentSet", etc.
  typed_object: string;
  schema_version: string;
  // Producer Tile that emitted this artifact, read from the artifact's own
  // provenance block (`produced_by.tile_name`). Null for user-uploaded
  // inputs (Molecule, ProteinStructure) the pilot user seeded — via the SDK
  // (`client.types.Molecule.from_sdf`) or the portal's SDF upload control
  // (POST /api/molecules), neither of which has a producing Tile.
  producer_tile: string | null;
  // Run that produced this artifact. Null for the same uploads.
  run_id: string | null;
  project_slug: string;
  created_at: string;
  // Byte size of the JSON payload on disk. Optional because
  // some catalog rows have streaming payloads where the size is
  // only known after the artifact materializes from S3.
  size_bytes: number | null;
  // Bundle-level acceptance flag surfaced on typed objects that
  // carry it (ForceFieldBundle, TorsionRefinementResult — anchor §3.3
  // / P1-FULL-AFFDO §7.3). Null for typed objects that don't carry
  // the field (Molecule, ProteinStructure) and for v1.0 bundles
  // read pre-migration. M9 #60 acceptance #2 — the ABFE submission
  // surface refuses "failed" upstream inputs without an explicit
  // override; the picker UI keys on this flag.
  scientific_status: "accepted" | "flagged" | "failed" | null;
}

/**
 * One distinct value of a feed dimension and how many artifacts carry it.
 *
 * `value` is null for the absent bucket — an upload has no run and no
 * producing Tile, most typed objects carry no `scientific_status`. Select
 * that bucket back with the dimension's sentinel below.
 *
 * **These counts are catalog-wide under the active filters** (flowstack#963).
 * The server produces them with a GROUP BY over the filtered table, not by
 * counting the rows it returned, which is what makes it safe to render one as
 * a plain number instead of hedging it as "loaded".
 */
export interface ArtifactFacetValue {
  value: string | null;
  count: number;
}

export interface ArtifactFacets {
  typed_object: ArtifactFacetValue[];
  producer_tile: ArtifactFacetValue[];
  scientific_status: ArtifactFacetValue[];
  /** Capped at the server's most-recent-runs limit; see {@link ArtifactQuery}. */
  run_id: ArtifactFacetValue[];
}

/**
 * One page of the feed plus what it takes to describe it honestly.
 *
 * `total` is how many artifacts MATCHED the filters, independent of how many
 * fitted on the page — a real COUNT(*), not a row count. `facets` and
 * `group_counts` are null unless the request asked for them.
 */
export interface ArtifactPage {
  artifacts: Artifact[];
  total: number;
  limit: number;
  offset: number;
  facets: ArtifactFacets | null;
  group_counts: ArtifactFacetValue[] | null;
}

/**
 * Filter token for "this artifact was not produced by a run" — i.e. a
 * user-uploaded ligand or structure.
 *
 * Mirrors `NO_RUN` in `src/flowstack/catalog/feed.py`. These three sentinels
 * are a wire contract with the server, which turns each into an `IS NULL`
 * predicate; changing one here without changing it there silently returns the
 * unfiltered feed.
 */
export const NO_RUN_KEY = "__no_run__";
/** Filter token for "no producing Tile" — the same uploads, other dimension. */
export const NO_TILE_KEY = "__no_tile__";
/** Filter token for "this typed object carries no scientific_status". */
export const STATUS_NONE = "__none__";

/** The closed vocabulary of `scientific_status`, per anchor §3.3. */
export const SCIENTIFIC_STATUS_VALUES = ["accepted", "flagged", "failed"] as const;

/** Dimensions the feed can be grouped by, matching the server's `group_by`. */
export const GROUPABLE_DIMENSIONS = [
  "typed_object",
  "run_id",
  "producer_tile",
  "scientific_status",
] as const;

export type GroupDimension = (typeof GROUPABLE_DIMENSIONS)[number];

/**
 * Everything `GET /api/artifacts` can be asked.
 *
 * Each filter becomes a SQL predicate applied BEFORE the limit
 * (flowstack#963), so a match outside the page is still found — which is the
 * difference between this and the client-side filtering it replaced.
 */
export interface ArtifactQuery {
  typedObject?: string;
  /** A run id, or {@link NO_RUN_KEY} for artifacts no run produced. */
  runId?: string;
  /** A Tile name, or {@link NO_TILE_KEY} for user-supplied artifacts. */
  producerTile?: string;
  /** A status, or {@link STATUS_NONE} for typed objects carrying none. */
  scientificStatus?: string;
  /** Substring match over content hash, type, producing Tile and run id. */
  q?: string;
  limit?: number;
  offset?: number;
  /** Ask for per-dimension value counts (four extra aggregates server-side). */
  facets?: boolean;
  groupBy?: GroupDimension;
}

/**
 * How many artifacts one page holds by default.
 *
 * Unlike the constant this replaces, it is now only a PAGE SIZE. It no longer
 * bounds what the feed can see: the server filters the whole catalog and
 * reports a real `total`, so a page that does not hold everything says so with
 * a number rather than with a warning (flowstack#963, closing the loop on the
 * notice #965/#966 had to render).
 */
export const ARTIFACT_PAGE_LIMIT = 200;

export const ARTIFACTS_QUERY_KEY = ["artifacts", "list"] as const;

/**
 * Cache key for the run-less window {@link useArtifacts} unions in.
 *
 * A child of {@link ARTIFACTS_QUERY_KEY} on purpose: `invalidateQueries` on
 * the parent prefix refreshes both windows, so the SDF-upload mutation in
 * `molecules.ts` keeps invalidating everything it should.
 */
export const ARTIFACT_UPLOADS_QUERY_KEY = ["artifacts", "list", "uploads"] as const;

/** Serialize a query into the endpoint's parameters, omitting the empty ones. */
export function artifactQueryParams(query: ArtifactQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.typedObject) params.set("typed_object", query.typedObject);
  if (query.runId) params.set("run_id", query.runId);
  if (query.producerTile) params.set("producer_tile", query.producerTile);
  if (query.scientificStatus) params.set("scientific_status", query.scientificStatus);
  if (query.q?.trim()) params.set("q", query.q.trim());
  params.set("limit", String(query.limit ?? ARTIFACT_PAGE_LIMIT));
  if (query.offset) params.set("offset", String(query.offset));
  if (query.facets) params.set("facets", "true");
  if (query.groupBy) params.set("group_by", query.groupBy);
  return params;
}

async function fetchArtifactPage(query: ArtifactQuery): Promise<ArtifactPage> {
  return apiFetch<ArtifactPage>(`/api/artifacts?${artifactQueryParams(query)}`);
}

/**
 * One filtered, counted page of the feed.
 *
 * The query object is the cache key, so two different filters are two cache
 * entries rather than one entry the components disagree about.
 */
export function useArtifactPage(query: ArtifactQuery) {
  return useQuery({
    queryKey: [...ARTIFACTS_QUERY_KEY, "page", artifactQueryParams(query).toString()],
    queryFn: () => fetchArtifactPage(query),
    staleTime: 30_000,
  });
}

/** Newest first, `content_hash` descending as tie-break — the server's order. */
function newestFirst(a: Artifact, b: Artifact): number {
  if (a.created_at !== b.created_at) return a.created_at < b.created_at ? 1 : -1;
  return a.content_hash < b.content_hash ? 1 : -1;
}

/**
 * The artifacts the submission pickers can offer: the newest page of the
 * catalog, UNIONED with the newest page of run-less uploads.
 *
 * Two windows rather than one, and this is the load-bearing part (Rupert
 * PR-513). A freshly-uploaded ligand must stay selectable however much
 * run-produced work has landed since — one `full_affdo_sequential` run leaves
 * 36 artifacts behind, so six of them would push an upload off a single
 * 200-row page and the user could no longer pick the thing they had just
 * uploaded. The endpoint used to protect that by reserving a slice of its page
 * for run-less rows; it cannot any more, because a row hoisted onto page 1
 * would then appear again at its natural rank on page 2. So the guarantee is
 * kept where it belongs — as a second query that asks for exactly those rows
 * (`run_id=__no_run__`, filtered in SQL before its own limit). Each window is
 * complete for what it asks; neither is a page carved out of the other.
 *
 * Returns the same `{data, isLoading, isError, error}` shape a `useQuery`
 * would, since that is what every caller already destructures.
 */
export function useArtifacts() {
  const feed = useQuery({
    queryKey: ARTIFACTS_QUERY_KEY,
    queryFn: async (): Promise<Artifact[]> =>
      (await fetchArtifactPage({ limit: ARTIFACT_PAGE_LIMIT })).artifacts,
    staleTime: 30_000,
  });
  const uploads = useQuery({
    queryKey: ARTIFACT_UPLOADS_QUERY_KEY,
    queryFn: async (): Promise<Artifact[]> =>
      (await fetchArtifactPage({ limit: ARTIFACT_PAGE_LIMIT, runId: NO_RUN_KEY })).artifacts,
    staleTime: 30_000,
  });

  const data = React.useMemo(() => {
    if (feed.data === undefined && uploads.data === undefined) return undefined;
    const merged = new Map<string, Artifact>();
    for (const artifact of [...(feed.data ?? []), ...(uploads.data ?? [])]) {
      merged.set(artifact.content_hash, artifact);
    }
    return [...merged.values()].sort(newestFirst);
  }, [feed.data, uploads.data]);

  return {
    data,
    isLoading: feed.isLoading || uploads.isLoading,
    isError: feed.isError || uploads.isError,
    error: feed.error ?? uploads.error,
  };
}

/**
 * One downloadable file belonging to an artifact (flowstack#619).
 *
 * `download_url` is a path on OUR API — an authenticated, tenant-scoped
 * streaming route — never a presigned S3 URL. Pass it to
 * {@link useDownloadPayload}, which ships the session cookie.
 */
export interface ArtifactPayload {
  payload_name: string;
  size_bytes: number | null;
  download_url: string;
}

export interface ArtifactDetail extends Artifact {
  download_url: string | null;
  download_url_expires_at: string | null;
  payloads: ArtifactPayload[];
}

export function artifactDetailQueryKey(contentHash: string) {
  return ["artifacts", "detail", contentHash] as const;
}

/**
 * Artifact detail, including its downloadable payloads.
 *
 * The backend added `payloads` in #619; an older backend simply omits the
 * field, so it is normalized to `[]` here rather than left undefined — the
 * download UI then renders its empty state instead of crashing on `.map`.
 */
export function useArtifactDetail(contentHash: string | null) {
  return useQuery({
    queryKey: artifactDetailQueryKey(contentHash ?? ""),
    enabled: contentHash !== null && contentHash !== "",
    queryFn: async (): Promise<ArtifactDetail> => {
      const detail = await apiFetch<ArtifactDetail>(
        `/api/artifacts/${encodeURIComponent(contentHash as string)}`
      );
      return { ...detail, payloads: detail.payloads ?? [] };
    },
    staleTime: 30_000,
  });
}

/**
 * Download one artifact payload through the authenticated API.
 *
 * A single mutation spanning fetch → blob → browser save, so the component
 * owns one `{isPending, isError}` surface rather than chaining three.
 */
export function useDownloadPayload() {
  return useMutation({
    mutationFn: async (payload: Pick<ArtifactPayload, "payload_name" | "download_url">) => {
      await apiDownload(payload.download_url, payload.payload_name);
    },
  });
}
